import { type ApolloClient, type NormalizedCacheObject } from "@apollo/client";
import { createApolloClient } from "@package/ui-apollo";
import { useAuthState } from "@package/ui-authentication";
import { useConfigState } from "@package/ui-config";
import {
  GetOrganizationListNameIdDocument,
  type GetOrganizationListNameIdQuery,
  type GetOrganizationListNameIdQueryVariables,
  type OrganizationModel,
} from "@package/ui-gql/index-data-access";
import { produce } from "immer";
import type { PropsWithChildren } from "react";
import { useCallback, useEffect, useState } from "react";
import { AUTH_TOKEN_FIELD_ORGANIZATIONS } from "../constants/constants";
import {
  defaultOrganizationState,
  OrganizationStateContext,
} from "./context-organization";

export function OrganizationStateProvider({ children }: PropsWithChildren) {
  const { token, tokenDecoded } = useAuthState();
  const { apollo, baseUrlApi, environment } = useConfigState();
  const [state, setState] = useState(defaultOrganizationState);
  const [client, setClient] = useState<ApolloClient<NormalizedCacheObject>>();

  const setSelectedOrganization = useCallback(
    (
      organization:
        | OrganizationModel
        | ((prev?: OrganizationModel) => OrganizationModel),
    ) => {
      setState((prev) =>
        produce(prev, (draft) => {
          draft.selectedOrganization =
            typeof organization === "function"
              ? organization(prev.selectedOrganization)
              : organization;
        }),
      );
    },
    [],
  );

  useEffect(
    function effectCreateApolloClient() {
      const create = async () => {
        if (baseUrlApi && environment && token) {
          setClient(
            await createApolloClient({
              ...apollo,
              environment,
              token,
              uri: `${baseUrlApi}/api/graphql`,
            }),
          );
        }
      };

      create();
    },
    [apollo, baseUrlApi, environment, token],
  );

  useEffect(
    function effectLoadOrganizations() {
      const fn = async () => {
        let success = true;
        let orgs: OrganizationModel[] = [];

        setState((prev) =>
          produce(prev, (draft) => {
            draft.loading = "pending";
          }),
        );

        try {
          const output = await client?.query<
            GetOrganizationListNameIdQuery,
            GetOrganizationListNameIdQueryVariables
          >({
            query: GetOrganizationListNameIdDocument,
            variables: {
              orgIds:
                tokenDecoded?.["https://v1.panoscape.app/organizations"] ?? [],
            },
          });

          if (Array.isArray(output?.data?.items)) {
            orgs = output.data?.items ?? [];
          }
        } catch (e) {
          console.error("[context-organization-provider]: Error", e);
          success = false;
          orgs = [];
        }

        setState((prev) =>
          produce(prev, (draft) => {
            draft.loading = success ? "succeeded" : "failed";
            draft.organizations = orgs;

            if (
              !draft.selectedOrganization ||
              !orgs.find((o) => o.orgId === draft.selectedOrganization?.orgId)
            ) {
              draft.selectedOrganization = orgs?.[0];
            }
          }),
        );
      };

      if (tokenDecoded?.[AUTH_TOKEN_FIELD_ORGANIZATIONS]?.length) {
        fn();
      } else {
        setState((prev) =>
          produce(prev, (draft) => {
            draft.loading = "failed";
            draft.organizations = [];
          }),
        );
      }
    },
    [baseUrlApi, client, token, tokenDecoded],
  );

  return (
    <OrganizationStateContext.Provider
      value={{
        ...state,
        setSelectedOrganization,
      }}
    >
      {children}
    </OrganizationStateContext.Provider>
  );
}
